import React, { useMemo, useState } from 'react';
import { AppButton, Box, ShimmerBox } from '@common-fe/common-fe';
import styled from 'styled-components';

import { LARGE_SIZE_LOGO, LetterIconSizeEnum, OrganizationTypes } from '@/common/constants';
import Permissions from '@/common/permissions';
import ROUTES from '@/common/routes';
import { Icon } from '@/components/elements/Icon/Icon';
import { Breadcrumbs } from '@/modules/core/components/Topbar/Breadcrumbs/Breadcrumbs';
import { useHasAccess, useHistory } from '@/modules/core/hooks';
import { EmployerStatus } from '@/modules/employer/employer.constants';
import { useAuthorization } from '@/modules/user/hooks';
import { useAuthStore } from '@/modules/user/stores';
import baseTheme from '@/styles/theme';

import { ExpressSetupSummaryModal } from './ExpressSetupSummaryModal/ExpressSetupSummaryModal';
import { AbandonedBanner } from './AbandonedBanner';
import { ExpressSetup } from './ExpressSetup.types';
import ExpressSetupLeaveDropdown from './ExpressSetupLeaveDropdown';
import { MoveToAdvanceSetupModal } from './MoveToAdvanceSetupModal';

interface WrapperProps {
  isSticky?: boolean;
}

const FixedWrapper = styled(Box)<WrapperProps>`
  position: fixed;
  width: 100%;
  height: fit-content;
  top: 0;
  z-index: 1000;
  background-color: ${({ theme }) => theme.colors.canvas} ;
`;

export interface Props {
  expressSetupData?: ExpressSetup;
  isLoading?: boolean;
  isSticky?: boolean;
  isShimmerHide?: boolean;
  onClearCache?: () => void;
}

const ExpressSetupTopbar: React.FC<Props> = ({
  expressSetupData,
  isLoading,
  isSticky,
  isShimmerHide,
  onClearCache,
}) => {
  const [isMoveToAdvanceModalVisible, setIsMoveToAdvanceModalVisible] = useState(false);
  const [isSummaryModalVisible, setIsSummaryModalVisible] = useState(false);
  const history = useHistory();
  const { user } = useAuthStore();
  const { handleLogout } = useAuthorization();
  const isExpressSetupUser = useMemo(() => user?.status && user?.status === EmployerStatus.ExpressSetup, [user]);
  const isCanMoveToAdvanceSetup = useHasAccess([
    { permission: Permissions.EXPRESS_SETUP },
  ]) && !expressSetupData?.isAbandoned;

  const isExpressSetupOrg = useMemo(
    () => user?.organizationType === OrganizationTypes.partner
    || user?.organizationType === OrganizationTypes.distributor
    || user?.organizationType === OrganizationTypes.system
    , [user]);

  return (
    <>
      <FixedWrapper
        data-testid="express-setup-topbar"
        isSticky={isSticky}
      >
        {
          expressSetupData?.isAbandoned && (
            <AbandonedBanner
              orgId={expressSetupData?.orgId}
              orgName={expressSetupData?.name}
            />
          ) 
        }
        <Box data-testid="topbar" border={{ size: 'small', color: 'border1', side: 'bottom' }} style={{ position: 'relative' }}>
          <Box
            style={{
              position: 'absolute',
              left: '24px',
              top: '24px',
              cursor: 'pointer',
            }}
          >
            <ExpressSetupLeaveDropdown
              options={
                [
                  ...(!isExpressSetupUser ? [{
                    title: 'Go back to admin portal',
                    redirectFunc: () => {
                      if (history) history.push(ROUTES.EMPLOYERS);
                      if (onClearCache) onClearCache();
                    },
                  }] : []),
                  ...(isCanMoveToAdvanceSetup && isExpressSetupOrg ? [{
                    title: 'Use advanced setup',
                    redirectFunc: () => setIsMoveToAdvanceModalVisible(true),
                  }] : []),
                  {
                    title: 'Log out',
                    warning: true,
                    redirectFunc: handleLogout,
                  }
                ]
              }
            />
          </Box>
          <Box align="center" pad={{ horizontal: 'l' }}>
            <Box width={baseTheme.defaultMinContentWidth}>
              <Box
                style={{ transition: 'all 0.6s' }}
                height={{ min: '72px' }}
                width="100%"
                direction="row"
                align="center"
                justify="between"
              >
                <Box direction="row">
                  {
                    isLoading && !isShimmerHide ? <ShimmerBox width="119px" height="40px" round="button2Round" margin={{ right: 'l' }}/> : (
                      <Box
                        onClick={() => history.push(ROUTES.EXPRESS_SETUP_BY_ID(expressSetupData?.orgId))}
                        margin={{ right: 'l' }}
                        data-testid="express-setup-topbar-logo"
                      >
                        <Icon
                          size={LetterIconSizeEnum.LOGO}
                          text="name"
                          height={LARGE_SIZE_LOGO.height}
                          width={LARGE_SIZE_LOGO.width}
                          src={expressSetupData?.logo}
                        />
                      </Box>
                    )
                  }
                  <Breadcrumbs expressSetupMode />
                </Box>

                <Box>
                  <AppButton
                    onClick={() => setIsSummaryModalVisible(true)}
                  >
                    View Summary
                  </AppButton>
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>
      </FixedWrapper>
      <MoveToAdvanceSetupModal
        visible={isMoveToAdvanceModalVisible}
        orgPath={expressSetupData?.orgPath}
        onSetVisible={setIsMoveToAdvanceModalVisible}
      />
      <ExpressSetupSummaryModal
        visible={isSummaryModalVisible}
        onSetVisible={setIsSummaryModalVisible}
      />
    </>
  );
};

export default ExpressSetupTopbar;
